import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Camera, Video, Maximize2, Minimize2, RefreshCw, 
  Settings, Grid, LayoutGrid, AlertCircle, Wifi, WifiOff
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';
import HLSPlayer from './HLSPlayer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LiveCameraViewer = ({ onOpenSettings }) => {
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [layout, setLayout] = useState('grid'); // grid, single
  const [refreshKey, setRefreshKey] = useState(0);

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  // Fetch cameras
  const { data: cameras = [], isLoading, refetch } = useQuery({
    queryKey: ['cameras', refreshKey],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/cameras`, { headers });
      if (!res.ok) return [];
      return res.json();
    },
    refetchInterval: 60000 // Refresh camera list every minute
  });

  const activeCameras = cameras.filter(c => c.is_active);

  const handleRefresh = () => {
    setRefreshKey(k => k + 1);
    refetch();
    toast.success('Refreshing camera feeds...');
  };

  const CameraFeed = ({ camera, fullscreen = false }) => {
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);

    const feedStyle = fullscreen 
      ? "w-full h-[70vh] object-contain bg-black" 
      : "w-full h-48 object-cover bg-gray-900";

    const streamType = (camera.stream_type || '').toLowerCase();

    // Browser-native HLS via backend transcoding proxy (RTSP -> HLS).
    // Covers the most common camera protocols: RTSP, HLS, generic HTTP.
    if (streamType === 'rtsp' || streamType === 'hls' || streamType === 'http') {
      return (
        <div className={fullscreen ? 'w-full h-[70vh]' : 'w-full h-48'}>
          <HLSPlayer cameraId={camera.id || camera._id} />
        </div>
      );
    }

    if (streamType === 'mjpeg') {
      return (
        <div className="relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
              <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
            </div>
          )}
          {error ? (
            <div className="flex items-center justify-center bg-gray-900 text-gray-400 h-48">
              <div className="text-center">
                <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                <p className="text-sm">Connection failed</p>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="mt-2"
                  onClick={() => { setError(false); setLoading(true); }}
                >
                  Retry
                </Button>
              </div>
            </div>
          ) : (
            <img
              src={camera.stream_url}
              alt={camera.name}
              className={feedStyle}
              onLoad={() => setLoading(false)}
              onError={() => { setError(true); setLoading(false); }}
            />
          )}
        </div>
      );
    }

    // Unknown stream type — fall back to copy-to-clipboard hint
    return (
      <div className="flex items-center justify-center bg-gray-900 text-gray-400 h-48">
        <div className="text-center p-4">
          <Video className="h-10 w-10 mx-auto mb-3" />
          <p className="font-medium">{camera.stream_type?.toUpperCase() || 'UNKNOWN'} Stream</p>
          <p className="text-xs mt-1 text-gray-500">Unsupported stream type</p>
          <code className="block mt-2 text-xs bg-gray-800 text-green-400 p-2 rounded break-all max-w-xs">
            {camera.stream_url}
          </code>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="text-center py-12 text-gray-500">
        <RefreshCw className="h-8 w-8 mx-auto animate-spin mb-4" />
        <p>Loading cameras...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="live-camera-viewer">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Camera className="h-6 w-6" />
            Live Camera Feeds
          </h2>
          <p className="text-sm text-gray-500">
            {activeCameras.length} active camera{activeCameras.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setLayout(layout === 'grid' ? 'single' : 'grid')}
          >
            {layout === 'grid' ? <LayoutGrid className="h-4 w-4" /> : <Grid className="h-4 w-4" />}
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
          {onOpenSettings && (
            <Button variant="outline" size="sm" onClick={onOpenSettings}>
              <Settings className="h-4 w-4 mr-1" />
              Settings
            </Button>
          )}
        </div>
      </div>

      {/* No Cameras Message */}
      {cameras.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Camera className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium mb-2">No Cameras Configured</h3>
            <p className="text-sm text-gray-500 mb-4">
              Add your network cameras to start monitoring
            </p>
            {onOpenSettings && (
              <Button onClick={onOpenSettings}>
                <Settings className="h-4 w-4 mr-2" />
                Configure Cameras
              </Button>
            )}
          </CardContent>
        </Card>
      ) : activeCameras.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <WifiOff className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium mb-2">No Active Cameras</h3>
            <p className="text-sm text-gray-500 mb-4">
              All cameras are currently disabled
            </p>
            {onOpenSettings && (
              <Button variant="outline" onClick={onOpenSettings}>
                <Settings className="h-4 w-4 mr-2" />
                Manage Cameras
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Camera Grid */}
          <div className={
            layout === 'grid' 
              ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
              : "space-y-4"
          }>
            {activeCameras.map((camera) => (
              <Card key={camera.id} className="overflow-hidden" data-testid={`camera-feed-${camera.id}`}>
                <CardHeader className="py-2 px-4 bg-gray-50 border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                      <CardTitle className="text-sm font-medium">{camera.name}</CardTitle>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {camera.location?.replace('_', ' ')}
                      </Badge>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => setSelectedCamera(camera)}
                      >
                        <Maximize2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <CameraFeed camera={camera} />
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Inactive Cameras List */}
          {cameras.filter(c => !c.is_active).length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Inactive Cameras ({cameras.filter(c => !c.is_active).length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {cameras.filter(c => !c.is_active).map(camera => (
                  <Badge key={camera.id} variant="secondary" className="text-xs">
                    <WifiOff className="h-3 w-3 mr-1" />
                    {camera.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Fullscreen Dialog */}
      <Dialog open={!!selectedCamera} onOpenChange={(open) => !open && setSelectedCamera(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              {selectedCamera?.name}
              <Badge variant="outline" className="ml-2 text-xs">
                {selectedCamera?.location?.replace('_', ' ')}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          {selectedCamera && (
            <div className="rounded-lg overflow-hidden">
              <CameraFeed camera={selectedCamera} fullscreen />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LiveCameraViewer;
